import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import { TableHeader } from '@Features/generics/generics.type';
import { useTypedSelector } from '@Features/store';
import { studentsGetSingle } from '@Pages/Students/Slice/Students.Slice';
import { useSnackbar } from '@Providers/useSnackbar';
import { Chip, TableCell, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import NotificationRecipientCell from '@Pages/ClassRooms/Pages/SingleCourse/Pages/CourseForm/Pages/CourseReports/Components/Tests/Components/NotificationRecipientCell';
import {
	StudentNotificationRow,
	StudentNotificationsMeta,
	StudentNotificationsRequester,
} from './Services/StudentNotifications.requester';

const TABLE_HEADS: Array<TableHeader> = [
	{ displayName: 'Date', width: '11%' },
	{ displayName: 'Type', width: '9%' },
	{ displayName: 'Instructor', width: '12%' },
	{ displayName: 'Course', width: '14%' },
	{ displayName: 'To', width: '8%' },
	{ displayName: 'Phone', width: '10%' },
	{ displayName: 'Message', width: '22%' },
	{ displayName: 'Status', width: '10%' },
];

/**
 * Student → Notifications: every message the platform tried to send about
 * this student — exam results (SMS + WhatsApp, the student backend's
 * outbox) and center attendance (WhatsApp, the admin backend's outbox) —
 * newest first, with a per-row WhatsApp retry. Attendance rows show the
 * rendered text; test rows show template + parameters (the exam outbox
 * stores no rendered body).
 */
const Notifications = () => {
	// The URL segment is the student's UUID (StudentsTable navigates with
	// student.uuid); the profile endpoints take the numeric users id, so
	// resolve it through the Students slice like the sibling tabs do.
	const { id } = useParams<{ id: string }>();
	const dispatch = useDispatch();
	const { currentEntity } = useTypedSelector((state) => state.Students);
	const userId = currentEntity?.id as number | undefined;
	const { displaySnackbar } = useSnackbar();
	const requester = StudentNotificationsRequester.getInstance();

	const [rows, setRows] = useState<StudentNotificationRow[]>([]);
	const [meta, setMeta] = useState<StudentNotificationsMeta | null>(null);
	const [resending, setResending] = useState(false);
	const [resendTarget, setResendTarget] =
		useState<StudentNotificationRow | null>(null);

	useEffect(() => {
		dispatch(studentsGetSingle({ id }));
	}, [id]);

	// Pagination fires on mount, usually before the student resolves; the
	// effect below fetches once the users id is known, and the ref keeps
	// the two from double-loading when it is already in the store.
	const loadedForRef = useRef<number | null>(null);
	const load = useCallback(
		async (page = 1, perPage = 10) => {
			if (!userId) return;
			loadedForRef.current = userId;
			try {
				const {
					data: { data, meta: freshMeta },
				} = await requester.list({ student_id: userId, page, perPage });
				setRows(data);
				setMeta(freshMeta);
			} catch (error: any) {
				displaySnackbar(
					'error',
					error?.response?.data?.message ?? 'Unable to load the notifications log'
				);
			}
		},
		[userId]
	);

	useEffect(() => {
		if (userId && loadedForRef.current !== userId) load(1, 10);
	}, [userId, load]);

	const onPageChangeRequest = useCallback(
		({ activePage, perPage }: onPageChangeRequestMeta) => {
			load(activePage, perPage ?? 10);
		},
		[load]
	);

	const performResend = async (row: StudentNotificationRow) => {
		if (!userId) return;
		setResending(true);
		try {
			await requester.resend({
				student_id: userId,
				kind: row.kind,
				checkin_id: row.checkin_id,
				attempt_id: row.attempt_id,
				recipient: row.recipient,
			});
			displaySnackbar('success', `WhatsApp message to the ${row.recipient} re-queued`);
			load(meta?.current_page ?? 1, meta?.per_page ?? 10);
		} catch (error: any) {
			displaySnackbar(
				'error',
				error?.response?.data?.message ?? 'Unable to retry the message'
			);
		} finally {
			setResending(false);
		}
	};

	const requestResend = (row: StudentNotificationRow) => {
		if (row.status === 'delivered' || row.status === 'read') {
			setResendTarget(row);
		} else {
			performResend(row);
		}
	};

	const messageCell = (row: StudentNotificationRow) => {
		if (row.message_body) {
			return (
				<Tooltip title={row.message_body}>
					<MessageText dir="auto">{row.message_body}</MessageText>
				</Tooltip>
			);
		}
		const params = (row.params ?? []).join(' · ');
		return (
			<Tooltip title={params || row.template || ''}>
				<MessageText>
					{row.template ?? '—'}
					{params ? ` — ${params}` : ''}
				</MessageText>
			</Tooltip>
		);
	};

	return (
		<>
			<EdConfirmModal
				open={!!resendTarget}
				text={`This WhatsApp message was already delivered. Send it again to the ${
					resendTarget?.recipient ?? ''
				}?`}
				handleClose={(close, confirm) => {
					const target = resendTarget;
					setResendTarget(null);
					if (confirm && target) performResend(target);
				}}
			/>
			<MainTable
				total={meta?.total ?? 0}
				pageNum={meta?.current_page}
				tableFilters={[]}
				tableHeads={TABLE_HEADS}
				totalPages={meta?.last_page ?? 0}
				RowsperPage={Number(meta?.per_page ?? 10)}
				onPageChangeRequest={onPageChangeRequest}
				renderItems={() =>
					rows.map((row) => (
						<MainTableRow key={`${row.kind}-${row.message_id}`}>
							<CellStyled>
								{new Date(row.created_at).toLocaleString([], {
									dateStyle: 'short',
									timeStyle: 'short',
								})}
							</CellStyled>
							<CellStyled>
								<Chip
									size="small"
									label={row.kind === 'attendance' ? 'Attendance' : 'Test'}
									sx={{
										backgroundColor:
											row.kind === 'attendance' ? '#5ac0fc' : '#6c63ff',
										color: '#fff',
									}}
								/>
							</CellStyled>
							<CellStyled>{row.instructor_name || '-'}</CellStyled>
							<CellStyled>
								{row.course_name}
								{row.test_name && <SubText>{row.test_name}</SubText>}
							</CellStyled>
							<CellStyled>
								{row.recipient === 'parent' ? 'Parent' : 'Student'}
							</CellStyled>
							<CellStyled>{row.phone_e164 ?? '-'}</CellStyled>
							<CellStyled>{messageCell(row)}</CellStyled>
							<CellStyled>
								<NotificationRecipientCell
									who={row.recipient === 'parent' ? 'Parent' : 'Student'}
									channel={row.channel}
									status={row.status as any}
									errorCode={row.error_code}
									error={row.last_error}
									updatedAt={row.updated_at}
									resending={resending}
									onResend={
										row.channel === 'whatsapp'
											? () => requestResend(row)
											: undefined
									}
								/>
							</CellStyled>
						</MainTableRow>
					))
				}
			/>
		</>
	);
};

const CellStyled = styled(TableCell)`
	font-size: 0.8125rem;
`;

const MessageText = styled.div`
	max-width: 20rem;
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	white-space: normal;
`;

const SubText = styled.div`
	font-size: 0.7rem;
	color: #a4a4a4;
`;

export default Notifications;
